<template>
  <v-dialog
    v-model="showDialog"
    max-width="600"
    persistent
    scrollable
    @click:outside="handleClose"
  >
    <v-card class="notification-detail-card">
      <!-- Header -->
      <v-card-title
        class="notification-header"
        :class="getHeaderClass(notification.type)"
      >
        <span class="notification-title">{{ notification.subject }}</span>
        <v-btn icon dark @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="pt-5">
        <!-- Notifikacija sadržaj -->
        <div
          class="notification-text"
          v-html="notification.text"
          @scroll="onScroll"
          ref="notificationText"
        ></div>

        <!-- CONFIRMATION tip - potvrda -->
        <div v-if="notification.type === 'CONFIRMATION'" class="confirmation-section">
          <div
            class="scroll-warning"
            :class="{ 'warning-active': !scrolled }"
          >
            <v-icon :color="scrolled ? 'success' : 'error'" class="mr-2">
              {{ scrolled ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <b>{{ scrolled ? 'Tekst pročitan' : 'Molimo pročitajte cijeli tekst!' }}</b>
          </div>

          <p class="mt-4">
            Potvrdite čitanje obavijesti upisivanjem riječi:
            <span class="keyword-display" v-html="keywordDisplay"></span>
          </p>

          <v-text-field
            v-model="confirmationText"
            outlined
            dense
            label="Unesite ključnu riječ"
            :disabled="!scrolled"
            @keyup.enter="submit"
          ></v-text-field>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="handleClose"
        >
          Odustani
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!canConfirm"
          @click="submit"
        >
          Potvrdi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { auth, df } from '@/plugins/firebase'
import state from '@/state'

export default {
  name: 'NotificationDetailDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    notification: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      confirmationText: '',
      scrolled: false
    }
  },
  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },

    canConfirm () {
      if (this.notification.type === 'CONFIRMATION') {
        return this.scrolled && this.checkConfirmation()
      }
      return true
    },

    keywordDisplay () {
      if (!this.notification.keyword) return ''

      if (this.confirmationText !== '') {
        const lowerKeyword = this.notification.keyword.toUpperCase()
        return lowerKeyword.replace(
          this.confirmationText.toUpperCase(),
          `<span style="color:green; font-weight: bold;">${this.confirmationText.toUpperCase()}</span>`
        )
      }

      return `<strong>${this.notification.keyword}</strong>`
    }
  },
  watch: {
    show (newVal) {
      if (newVal) {
        // Reset stanja kada se otvara modal
        this.confirmationText = ''
        this.scrolled = false

        // Provjeri da li je skrolanje potrebno
        this.$nextTick(() => {
          this.checkIfScrollNeeded()
        })
      }
    }
  },
  methods: {
    getHeaderClass (type) {
      switch (type) {
        case 'CONFIRMATION':
        case 'WARNING':
          return 'header-warning'
        case 'IMPORTANT':
          return 'header-important'
        default:
          return 'header-info'
      }
    },

    onScroll (e) {
      const { scrollTop, offsetHeight, scrollHeight } = e.target
      if ((scrollTop + offsetHeight) >= (scrollHeight - 5)) {
        this.scrolled = true
      }
    },

    checkIfScrollNeeded () {
      const textElement = this.$refs.notificationText
      if (textElement) {
        // Ako nema potrebe za skrolanjem, označi kao scrolled
        if (textElement.scrollHeight <= textElement.offsetHeight) {
          this.scrolled = true
        }
      }
    },

    checkConfirmation () {
      if (!this.notification.keyword) return false

      const options = { sensitivity: 'base' }
      return this.notification.keyword.localeCompare(
        this.confirmationText,
        undefined,
        options
      ) === 0
    },

    async submit () {
      if (!this.canConfirm) return

      try {
        const userID = state.getCashRegisterUser()?.id || state.getUser().id
        const userName = state.getCashRegisterUser()?.name
          ? `${state.getCashRegisterUser().name} ${state.getCashRegisterUser().surname}`
          : `${state.getUser().name} ${state.getUser().surname}`

        await df.doc(
          `user_notifications/${auth.currentUser.uid}/notifications/${this.notification.id}`
        ).update({
          readed: true,
          reader_id: userID,
          reader_name: userName,
          readed_time: parseInt(Date.now() / 1000)
        })

        this.$emit('confirmed')
        this.handleClose()
      } catch (error) {
        console.error('Error updating notification:', error)
        // Možda dodati toast poruku za error
      }
    },

    handleClose () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.notification-detail-card {
  border-radius: 16px;
  overflow: hidden;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: white;
}

.notification-header.header-warning {
  background: linear-gradient(135deg, #f57c02, #ff9800);
}

.notification-header.header-important {
  background: linear-gradient(135deg, #1577da, #3989c9);
}

.notification-header.header-info {
  background: linear-gradient(135deg, #3989c9, #00b3be);
}

.notification-title {
  font-size: 20px;
  font-weight: 600;
  flex: 1;
  margin-right: 16px;
}

.notification-text {
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.notification-text::-webkit-scrollbar {
  width: 6px;
}

.notification-text::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.notification-text::-webkit-scrollbar-thumb {
  background-color: rgba(57, 137, 201, 0.3);
  border-radius: 3px;
  transition: background-color 0.3s ease;
}

.notification-text::-webkit-scrollbar-thumb:hover {
  background-color: rgba(57, 137, 201, 0.5);
}

.confirmation-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid rgba(0, 0, 0, 0.08);
}

.scroll-warning {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  background: rgba(76, 175, 80, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.scroll-warning.warning-active {
  background: rgba(244, 67, 54, 0.1);
  border-color: rgba(244, 67, 54, 0.3);
}

.keyword-display {
  margin-left: 8px;
  padding: 4px 8px;
  background: rgba(57, 137, 201, 0.1);
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  border: 1px solid rgba(57, 137, 201, 0.3);
}
</style>
